// src/services/shipmentService.ts
import apiClient from "./apiClient";
import {
  ShipmentsApiResponse,
  ShipmentDetailApiResponse,
  Shipment,
  ShipmentSettingsApiResponse,
} from "../types/shipment";
import { toLegacyListResponse } from "../utils/normalizePaginatedResponse";

interface ShipmentStatusUpdate {
  shipmentId: string;
  newStatus:
    | "PENDING"
    | "IN_TRANSIT"
    | "SHIPPED"
    | "PACKED"
    | "CHECKED_IN"
    | "CHECKED_OUT"
    | "NOT_CHECKED"
    | "ARRIVED_AT_LOCAL_HUB"
    | "ARRIVED_AT_NIGERIA_HUB"
    | "ARRIVED_AT_UK_HUB"
    | "COMPLETED"
    | "CANCELED"
    | "PROCESSING"
    | "DECLINED"
    | "ORDER_SUBMITTED"
    | "ORDER_PAID_SUCCESSFULLY"
    | "ORDER_PAYMENT_FAILED"
    | "ORDER_VERIFIED"
    | "AWAITING_SHIPMENT"
    | "FLIGHT_SEA_DEPARTURE"
    | "CUSTOM_CLEARANCE"
    | "ARRIVE_AT_THE_RECEIVING_HUB_CENTER";
  reason?: string;
}

// ... rest of the file remains the same ...
const SHIPMENTS_PATH = "shipments";

/**
 * The backend DTO uses `documentationFee` (the actual DB column); the admin UI
 * calls it "Clearance Fee". The API also runs a strict validation pipe
 * (forbidNonWhitelisted), so any extra/unknown property triggers a 400.
 * This builds a clean, whitelisted payload and maps clearanceFee -> documentationFee.
 */
const buildRatePayload = (data: any): Record<string, any> => {
  const payload: Record<string, any> = {
    fromCountry: data.fromCountry,
    toCountry: data.toCountry,
    currency: data.currency,
    deliveryDays:
      data.deliveryDays != null ? String(data.deliveryDays) : undefined,
    category: data.category,
    goodType: data.goodType,
    shipmentType: data.shipmentType,
    deliveryType: data.deliveryType,
  };

  if (data.pricePerKg !== undefined && data.pricePerKg !== "") {
    payload.pricePerKg = Number(data.pricePerKg);
  }

  // "Clearance Fee" in the UI is stored as documentationFee in the API/DB
  const fee = data.clearanceFee ?? data.documentationFee;
  if (fee !== undefined && fee !== "" && !Number.isNaN(Number(fee))) {
    payload.documentationFee = Number(fee);
  }

  // Backend validates weight with @Min(1); only send it when valid
  const weight = Number(data.weight);
  if (!Number.isNaN(weight) && weight >= 1) {
    payload.weight = weight;
  }

  // Drop empty/undefined so the strict validation pipe doesn't reject them
  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined || payload[key] === "") {
      delete payload[key];
    }
  });

  return payload;
};

/** Expose documentationFee back to the UI as clearanceFee. */
const withClearanceFee = (setting: any) => ({
  ...setting,
  clearanceFee: setting?.clearanceFee ?? setting?.documentationFee ?? 0,
});

export const getShipments = async (
  page = 1,
  limit = 9
): Promise<ShipmentsApiResponse> => {
  try {
    const response = await apiClient.get(SHIPMENTS_PATH, {
      params: { page, limit },
    });
    return toLegacyListResponse<Shipment, "shipments">(response.data, "shipments");
  } catch (error) {
    console.error("Failed to fetch shipments:", error);
    throw error;
  }
};

export const getShipmentById = async (id: string): Promise<Shipment> => {
  try {
    const response = await apiClient.get<ShipmentDetailApiResponse>(
      `${SHIPMENTS_PATH}/${id}`
    );

    // Extract the nested shipment object and return it directly.
    // This is what the component expects.
    return response.data.data.shipment;
  } catch (error) {
    console.error(`Failed to fetch shipment with id ${id}:`, error);
    throw error;
  }
};

export const getShipmentSettings = async (
  page = 1,
  limit = 9
): Promise<ShipmentSettingsApiResponse> => {
  try {
    const response = await apiClient.get<ShipmentSettingsApiResponse>(
      `${SHIPMENTS_PATH}/settings`,
      {
        params: { page, limit },
      }
    );
    const body = response.data;
    if (body?.data?.settings) {
      body.data.settings = body.data.settings.map(withClearanceFee);
    }
    return body;
  } catch (error) {
    console.error("Failed to fetch shipment settings:", error);
    throw error;
  }
};

export const createShipmentSettings = async (
  settingsData: any
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${SHIPMENTS_PATH}/create-rate`,
      buildRatePayload(settingsData)
    );
    return response.data;
  } catch (error) {
    console.error("Failed to create/update shipment settings:", error);
    throw error;
  }
};

export const updateShipmentRate = async (
  shippingRateId: string,
  updateData: any
): Promise<any> => {
  try {
    const response = await apiClient.patch(
      `${SHIPMENTS_PATH}/update/${shippingRateId}`,
      buildRatePayload(updateData)
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update shipping rate:", error);
    throw error;
  }
};

export const declineShipment = async (
  shipmentId: string,
  reason: string
): Promise<any> => {
  try {
    const response = await apiClient.patch(
      `${SHIPMENTS_PATH}/decline-shipment`,
      {
        shipmentId,
        reason,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to decline shipment:", error);
    throw error;
  }
};

export const updateShipmentStatus = async (
  updateData: ShipmentStatusUpdate
): Promise<any> => {
  try {
    // API EShipmentStatus values are lowercase ("in_transit"); the UI uses "IN_TRANSIT".
    const response = await apiClient.patch(`${SHIPMENTS_PATH}/update-status`, {
      shipmentId: updateData.shipmentId,
      newStatus: String(updateData.newStatus).toLowerCase(),
    });
    return response.data;
  } catch (error) {
    console.error("Failed to update shipment status:", error);
    throw error;
  }
};
